import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  LinearProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import CommonPagination from "../../components/Table/CommonPagination";
import { getInProgressMatches } from "../../http/inProgressMatches";
import CompetingOnCell from "./CompetingOnCell";
import {
  RANK_ORDER,
  NO_RANK_LABEL,
  FOR_FUN_LABEL,
  RANK_COLORS,
  ROWS_PER_PAGE,
  formatAge,
  formatAvgVotes,
  formatBoostMultiple,
  oldPagentBoostFactor,
  ageColor,
  formatMatchType,
  formatPagentLabel,
  formatRankLabel,
  rankChipSx,
  filterSelectSx,
  useSectionStyles,
} from "./matchDisplay";

const MATCH_TYPES = [
  "PROMPT_POST",
  "LIVE",
  "CONTEST_POST",
  "CATEGORY_POST",
  "KARAOKE_POST",
  "DANCE_POST",
  "ART_POST",
  "CHESS",
  "DEBATE",
  "GROUP_DUET_POST",
];

/**
 * Embeddable Active Matches section (summary, filters, paginated list).
 * Rendered on the Match & Energy page below the gameplay settings; owns its
 * data fetching with a local loading bar so it doesn't fight the host page's
 * loading state.
 *
 * @param {number} [refreshToken] - Bump to force a refetch from the host
 *   (e.g. after saving gameplay settings, which restamps and ends matches).
 * @param {string|number} [oldMatchFeedBoost] - Live old-pagent rate from the
 *   form above. Each row shows rate ^ floor(age / 15 min) under its age.
 */
const InProgressMatchesSection = ({ refreshToken = 0, oldMatchFeedBoost = 1 }) => {
  const classes = useSectionStyles();

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, byRank: [], byAge: [] });
  const [matches, setMatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rankFilter, setRankFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("oldest");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getInProgressMatches({
        page: page + 1,
        limit: ROWS_PER_PAGE,
        rank: rankFilter,
        matchType: typeFilter,
        sortOrder,
      });
      const data = res?.data || {};
      setSummary(data.summary || { total: 0, byRank: [], byAge: [] });
      setMatches(data.matches || []);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [page, rankFilter, typeFilter, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshToken]);

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  // Zero-fill rank rows so every tier (and For fun) is always visible;
  // UNKNOWN only if present.
  const byRankMap = new Map((summary.byRank || []).map((r) => [r.rank, r]));
  const rankRows = [...RANK_ORDER, FOR_FUN_LABEL, NO_RANK_LABEL]
    .filter((rank) => rank !== NO_RANK_LABEL || byRankMap.has(NO_RANK_LABEL))
    .map(
      (rank) =>
        byRankMap.get(rank) || {
          rank,
          count: 0,
          avgAgeMs: null,
          oldestAgeMs: null,
          avgVotes: null,
          medianDurationMs: null,
        }
    );
  const maxRankCount = Math.max(1, ...rankRows.map((r) => r.count));

  const ageRows = summary.byAge || [];
  const maxAgeCount = Math.max(1, ...ageRows.map((r) => r.count));

  const percentOfTotal = (count) =>
    summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#142C5C",
              fontSize: { xs: "18px", sm: "20px", md: "22px" },
              lineHeight: 1.3,
            }}
          >
            Active Matches
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, fontSize: "13px" }}
          >
            All in-progress matches with rank and age breakdown. Each rank shows
            the average votes on its in-progress matches and the median age of
            pagents currently open at that rank. Pagent # is which match each
            entry is on (1 is its first). Curated feed copies are excluded.
          </Typography>
        </Box>
        <Button
          variant="contained"
          className={classes.refreshButton}
          startIcon={<RefreshIcon />}
          onClick={fetchData}
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <LinearProgress sx={{ marginBottom: "16px", borderRadius: 2 }} />
      )}

      {/* Summary: rank breakdown + age distribution */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          flexWrap: "wrap",
          marginBottom: "24px",
        }}
      >
        <Box className={classes.summaryCard} sx={{ flex: "1 1 420px", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}
          >
            By Rank ({summary.total} in progress)
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#777", marginBottom: "12px" }}>
            Avg votes is the mean of both sides on in-progress matches. Median
            is the typical age of prompt, category, contest, karaoke, dance,
            art, and group pagents open at that rank right now. Live, chess,
            and debate are left out.
          </Typography>
          {rankRows.map((row) => (
            <Box key={row.rank} sx={{ marginBottom: "10px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "3px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <Chip
                    label={formatRankLabel(row.rank)}
                    size="small"
                    sx={rankChipSx(row.rank)}
                  />
                  <Typography sx={{ fontSize: "12px", color: "#666" }}>
                    {row.count} ({percentOfTotal(row.count)}%)
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "11px", color: "#888", whiteSpace: "nowrap" }}
                >
                  {row.count > 0
                    ? `avg age ${formatAge(row.avgAgeMs)} · oldest ${formatAge(
                        row.oldestAgeMs
                      )}`
                    : "—"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#555",
                  marginBottom: "4px",
                  lineHeight: 1.4,
                }}
              >
                {`avg ${formatAvgVotes(row.avgVotes)} votes · median ${
                  row.medianDurationMs != null
                    ? formatAge(row.medianDurationMs)
                    : "—"
                }`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(row.count / maxRankCount) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor:
                      RANK_COLORS[row.rank] || RANK_COLORS[NO_RANK_LABEL],
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        <Box className={classes.summaryCard} sx={{ flex: "1 1 320px", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}
          >
            By Age
          </Typography>
          {ageRows.map((row) => (
            <Box key={row.bucket} sx={{ marginBottom: "10px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "3px",
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: "#333" }}
                >
                  {row.bucket}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                  {row.count} ({percentOfTotal(row.count)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(row.count / maxAgeCount) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor: "#142C5C",
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Match list */}
      <Box className={classes.listSection}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 600 }}>
            Matches ({totalCount})
          </Typography>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label="Rank"
              value={rankFilter}
              onChange={handleFilterChange(setRankFilter)}
              sx={filterSelectSx}
            >
              <MenuItem value="">All ranks</MenuItem>
              {RANK_ORDER.map((rank) => (
                <MenuItem key={rank} value={rank}>
                  {rank}
                </MenuItem>
              ))}
              <MenuItem value={FOR_FUN_LABEL}>{formatRankLabel(FOR_FUN_LABEL)}</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Match Type"
              value={typeFilter}
              onChange={handleFilterChange(setTypeFilter)}
              sx={filterSelectSx}
            >
              <MenuItem value="">All types</MenuItem>
              {MATCH_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {formatMatchType(type)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Sort"
              value={sortOrder}
              onChange={handleFilterChange(setSortOrder)}
              sx={filterSelectSx}
            >
              <MenuItem value="oldest">Oldest first</MenuItem>
              <MenuItem value="newest">Newest first</MenuItem>
            </TextField>
          </Box>
        </Box>

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.stickyHeader}>
                <TableCell className={classes.headerCell}>Match ID</TableCell>
                <TableCell className={classes.headerCell}>Players</TableCell>
                <TableCell className={classes.headerCell}>Pagent #</TableCell>
                <TableCell className={classes.headerCell}>Type</TableCell>
                <TableCell className={classes.headerCell}>Competing On</TableCell>
                <TableCell className={classes.headerCell}>Rank</TableCell>
                <TableCell className={classes.headerCell}>Votes</TableCell>
                <TableCell className={classes.headerCell}>Age</TableCell>
                <TableCell className={classes.headerCell}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matches.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    sx={{
                      textAlign: "center",
                      height: "160px",
                      fontSize: "14px",
                      color: "#999",
                    }}
                  >
                    No in-progress matches found.
                  </TableCell>
                </TableRow>
              ) : (
                matches.map((match) => {
                  const feedBoost = oldPagentBoostFactor(
                    oldMatchFeedBoost,
                    match.ageMs,
                    match.matchType
                  );
                  return (
                  <TableRow key={match._id}>
                    <TableCell className={classes.bodyCell}>
                      <span className={classes.idText}>{match._id}</span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span>{match.player1 || "—"}</span>
                      <span className={classes.vsText}>vs</span>
                      <span>{match.player2 || "—"}</span>
                      {match.isGroupDuet && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "11px",
                            color: "#888",
                            marginLeft: "4px",
                          }}
                        >
                          (groups)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {!match.player1Pagent && !match.player2Pagent ? (
                        "—"
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{ fontWeight: 600, fontSize: "13px" }}
                          >
                            {formatPagentLabel(match.player1Pagent)}
                          </Typography>
                          <span className={classes.vsText}>vs</span>
                          <Typography
                            component="span"
                            sx={{ fontWeight: 600, fontSize: "13px" }}
                          >
                            {formatPagentLabel(match.player2Pagent)}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {formatMatchType(match.matchType)}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <CompetingOnCell
                        song={match.song}
                        challenge={match.challenge}
                        contest={match.contest}
                        missionNumber={match.missionNumber}
                        category={match.category}
                        isGroupDuet={match.isGroupDuet}
                        groupDuetTargetType={match.groupDuetTargetType}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {match.ladder === "for_fun" || match.rank === FOR_FUN_LABEL ? (
                        <Box>
                          <Chip
                            label={formatRankLabel(FOR_FUN_LABEL)}
                            size="small"
                            sx={rankChipSx(FOR_FUN_LABEL)}
                          />
                          {match.voteThreshold != null && (
                            <Typography sx={{ fontSize: "11px", color: "#888", mt: 0.5 }}>
                              First to {match.voteThreshold}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Chip
                          label={match.rank}
                          size="small"
                          sx={rankChipSx(match.rank)}
                        />
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {match.player1Votes}
                        </Typography>
                        <span className={classes.vsText}>:</span>
                        <Typography
                          component="span"
                          sx={{ fontWeight: 600, fontSize: "13px" }}
                        >
                          {match.player2Votes}
                        </Typography>
                      </Box>
                      {match.voteThreshold !== null &&
                        match.voteThreshold !== undefined && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: match.voteThresholdReached
                                ? "#2e7d32"
                                : "#888",
                            }}
                          >
                            First to {match.voteThreshold}
                            {match.voteThresholdReached ? " ✓" : ""}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: ageColor(match.ageMs),
                        }}
                      >
                        {formatAge(match.ageMs)}
                      </Typography>
                      {feedBoost != null && (
                        <Typography
                          sx={{ fontSize: "11px", color: "#888", display: "block" }}
                        >
                          {formatBoostMultiple(feedBoost)} feed
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {match.createdAt
                        ? dayjs(match.createdAt).format("MMM D, YYYY h:mm A")
                        : "—"}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <Box sx={{ marginTop: "12px" }}>
            <CommonPagination
              rowsPerPage={ROWS_PER_PAGE}
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InProgressMatchesSection;
